import { useCollection } from '@amzn/awsui-collection-hooks';
import { Box, Button, Header, Pagination, SpaceBetween, Table, TableProps } from '@amzn/awsui-components-core';
import React, { useCallback, useState } from 'react';
import { logger } from 'src/logger';
import { DEFAULT_FILE_NAME, DEFAULT_SHEET_NAME, formatTableDataForExport } from 'src/utilities/CloudscapeTableUtils';
import { exportAsExcelFile } from 'src/utilities/FileServices';
import { EmptyStateMessage } from '../EmptyStateMessage';

// Constants
const DEFAULT_PAGE_SIZE = 3;

// Base interface for our table component - contains all the common props
// that we need regardless of whether download is enabled or not
interface BaseDetailsTableProps {
  emptyMessage: string;
  filterProperties: any;
  visibleContent?: readonly string[];
  data: readonly Record<string, any>[];
  columnDefinitions: readonly TableProps.ColumnDefinition<any>[];
  pageSize?: number;
  headerName?: string;
  stickyHeader?: boolean;
  /**
   * Enables Cloudscape inline cell editing. Only takes effect for columns that also
   * declare `editConfig`. Omit to keep the table read-only (the default).
   */
  submitEdit?: TableProps<any>['submitEdit'];
}

// Props interface for when we don't want download functionality
// Note: downloadFileName is explicitly blocked here to prevent confusion
interface WithoutDownloadProps extends BaseDetailsTableProps {
  enableDownloadReport?: false;
  downloadFileName?: never; // Blocks downloadFileName when downloads are disabled
}

// Props interface for when we want download functionality enabled
// Forces downloadFileName to be provided since it's needed for the export
interface WithDownloadProps extends BaseDetailsTableProps {
  enableDownloadReport: true;
  downloadFileName: string; // Required for file export naming
}

// Union type that gives us compile-time checking of required props
// based on whether downloads are enabled or not
type DetailsTableProps = WithoutDownloadProps | WithDownloadProps;

/**
 * FinOpsTable Component
 * Displays data in a table format with filtering, pagination, and export capabilities
 */
export const FinOpsTable: React.FC<DetailsTableProps> = (props) => {
  const { enableDownloadReport = false } = props;
  logger.info('Rendering DetailsTableWithProps with data length:' + props.data.length);

  // Input validation
  if (!Array.isArray(props.data)) {
    logger.error('Invalid data prop provided:', props.data);
    throw new Error('Data must be an array');
  }

  // State
  const [preferences] = useState({
    pageSize: props.pageSize || DEFAULT_PAGE_SIZE,
    visibleContent: props.visibleContent
  });

  // Collection hook setup
  const { items, actions, collectionProps, paginationProps } = useCollection(props.data, {
    propertyFiltering: {
      filteringProperties: props.filterProperties,
      empty: <EmptyStateMessage title="No data" subtitle="No data to display." />,
      noMatch: (
        <EmptyStateMessage
          title="No matches"
          subtitle="No match found to display."
          action={<Button onClick={() => actions.setFiltering('')}>Clear Filter</Button>}
        />
      )
    },
    pagination: { pageSize: preferences.pageSize },
    sorting: {},
    selection: {}
  });

  /**
   * Handles the export functionality
   */
  const onDownloadClick = useCallback(() => {
    logger.info('Starting download process');
    try {
      const exportData = formatTableDataForExport(props.data, props.columnDefinitions);
      const fileName = props.downloadFileName ?? DEFAULT_FILE_NAME;
      exportAsExcelFile(exportData, fileName, DEFAULT_SHEET_NAME);
      logger.info('Download completed successfully');
    } catch (error: any) {
      logger.error('Error during download:', error);
    }
  }, [props.data, props.columnDefinitions, props.downloadFileName]);

  /**
   * Renders the table header with pagination and download button
   */
  const tableHeader = (
    <Box>
      <Box float="left">{props.headerName && <Header counter={`(${props.data.length})`}>{props.headerName}</Header>}</Box>
      <Box float="right">
        <SpaceBetween size="m" direction="horizontal">
          <Pagination {...paginationProps} />
          {enableDownloadReport && (
            <Button iconName="download" onClick={onDownloadClick} disabled={!props.data.length}>
              Download
            </Button>
          )}
        </SpaceBetween>
      </Box>
    </Box>
  );

  // Performance optimization for table props
  const tableProps = {
    variant: 'embedded' as const,
    ...collectionProps,
    columnDefinitions: props.columnDefinitions,
    items,
    loadingText: 'Loading details',
    visibleColumns: props.visibleContent,
    resizableColumns: true,
    stickyHeader: props.stickyHeader ?? true,
    empty: <EmptyStateMessage title="No data" subtitle={props.emptyMessage} />,
    header: tableHeader,
    // Only forwarded when supplied, so read-only callers are unaffected.
    ...(props.submitEdit ? { submitEdit: props.submitEdit } : {})
  };

  return <Table {...tableProps} />;
};
